import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Popover } from "@mui/material";
import * as React from "react";

import { Button } from "@/shared/foundation/primitives/Button";
import { usePopupState } from "@/shared/hooks/usePopupState";

import styles from "./DataTableFilters.module.css";
import type { FilterDefinition } from "./FilterDefinition";
import { FilterMenuBody } from "./FilterMenuBody";
import type { Filter } from "./FilterSchema";

export interface DataTableFiltersProps {
  /**
   * Filterable fields available in the picker. The component does not fetch
   * schema or data itself — the consumer is responsible for assembling this
   * list (typically a static set for built-in fields plus a dynamic set for
   * org-specific custom fields).
   */
  filterDefinitions: FilterDefinition[];

  /**
   * Fired when the user completes a single filter configuration.
   */
  onFilterSelected?: (filter: Filter) => void;
}

/**
 * Filter button + popover for `DataTable`. The menu lets the user enter
 * exactly one filter at a time and emits it via `onFilterSelected`.
 */
export function DataTableFilters({
  filterDefinitions,
  onFilterSelected,
}: DataTableFiltersProps) {
  const popup = usePopupState();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (popup.isOpen) {
      popup.close();
    } else {
      popup.open({ anchorEl: event.currentTarget });
    }
  };

  const handleCommit = (filter: Filter) => {
    onFilterSelected?.(filter);
    popup.close();
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<FilterAltIcon fontSize="small" />}
        className={styles.trigger}
        onClick={handleClick}
        aria-haspopup="dialog"
        aria-expanded={popup.isOpen}
      >
        <span className={styles.triggerLabel}>Filter</span>
      </Button>
      <Popover
        open={popup.isOpen}
        anchorEl={popup.anchor}
        onClose={popup.close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            className: styles.popover,
            // Popover content lives in the dialog landmark so screen readers
            // announce it as a transient widget anchored to the trigger.
            role: "dialog",
            "aria-label": "Filter menu",
          },
        }}
      >
        <FilterMenuBody
          filterDefinitions={filterDefinitions}
          onCommit={handleCommit}
          onCancel={popup.close}
        />
      </Popover>
    </>
  );
}
